import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Package, Truck, PencilSimple, FileText, Camera, EnvelopeSimple, ClockCounterClockwise,
  ChatCircleDots, CircleNotch, CheckCircle, MapPin, Broadcast,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * LoadTimelineDrawer — right-side drawer that renders the full chronological
 * story of a single load: creation → status changes → doc uploads (POD! BOL!)
 * → invoice email. Dispatch uses this to answer "what happened on this load?"
 * without scrolling through audit logs.
 */
const EVENT_STYLES = {
  load_created: { icon: Package, color: "text-white", ring: "ring-white/20" },
  "load.updated": { icon: PencilSimple, color: "text-neutral-400", ring: "ring-white/10" },
  "load.status_changed": { icon: Truck, color: "text-blue-400", ring: "ring-blue-500/30" },
  driver_pod_uploaded: { icon: Camera, color: "text-emerald-400", ring: "ring-emerald-500/40" },
  driver_upload: { icon: Camera, color: "text-emerald-300", ring: "ring-emerald-500/25" },
  doc_uploaded: { icon: FileText, color: "text-[#FF5A1F]", ring: "ring-[#FF5A1F]/40" },
  invoice_emailed: { icon: EnvelopeSimple, color: "text-amber-300", ring: "ring-amber-500/40" },
  // Geofence auto-status events — cyan reads as "system/telemetry"
  // and stays out of the status-change (blue) family so ops can tell
  // auto vs manual at a glance.
  geofence_arrived_pickup: { icon: MapPin, color: "text-cyan-400", ring: "ring-cyan-500/30" },
  geofence_in_transit: { icon: Broadcast, color: "text-cyan-400", ring: "ring-cyan-500/30" },
  geofence_arrived_delivery: { icon: MapPin, color: "text-cyan-300", ring: "ring-cyan-500/40" },
};

export default function LoadTimelineDrawer({ loadId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [thanking, setThanking] = useState(false);
  const [thanked, setThanked] = useState(false);

  useEffect(() => {
    if (!open || !loadId) return;
    setLoading(true);
    setThanked(false);
    client.get(`/loads/${loadId}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load timeline"))
      .finally(() => setLoading(false));
  }, [open, loadId]);

  // Any driver-uploaded POD in the timeline unlocks the thank-driver
  // action. Once tapped (or already thanked server-side), the button
  // flips to a "Thanked" state and disables itself.
  const hasDriverPod = !!(data?.events || []).find(
    (e) => e.type === "driver_pod_uploaded",
  );

  const thankDriver = async () => {
    if (!loadId) return;
    setThanking(true);
    try {
      const { data: resp } = await client.post(`/loads/${loadId}/thank-driver`);
      if (resp.status === "sent") {
        toast.success("Thanks SMS sent to driver ✓");
        setThanked(true);
      } else if (resp.status === "already_thanked") {
        toast.info("Driver already thanked for this load");
        setThanked(true);
      } else if (resp.status === "no_phone") {
        toast.warning("Driver has no phone on file");
        setThanked(true);
      } else {
        toast.warning(`SMS not sent: ${resp.reason || resp.status}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send thank-you SMS");
    } finally {
      setThanking(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={onClose}
      data-testid="load-timeline-backdrop"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-[#111] border-l border-white/10 h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="load-timeline-drawer"
      >
        <div className="sticky top-0 bg-[#111] border-b border-white/10 p-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#FF5A1F] flex items-center gap-1">
              <ClockCounterClockwise size={11} /> Delivery timeline
            </div>
            <h3 className="text-lg font-bold text-white mt-0.5">
              {data?.load?.load_number || "Load"}
            </h3>
            {data?.load && (
              <div className="text-xs text-white/50 mt-0.5 truncate">
                {data.load.origin} → {data.load.destination}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="load-timeline-close"
            className="p-1.5 text-white/50 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        {/* Thank-driver action bar — only shows when a driver POD exists.
            Contextual placement (right under the header) so ops sees it
            the moment they open the drawer for a load with fresh POD. */}
        {hasDriverPod && (
          <div
            data-testid="load-timeline-thank-driver-bar"
            className="border-b border-white/10 bg-emerald-500/[0.03] px-4 py-3 flex items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
                Driver POD received
              </div>
              <div className="text-xs text-white/70 mt-0.5">
                Send a quick "thanks — invoice going out" back to their phone.
              </div>
            </div>
            <button
              type="button"
              onClick={thankDriver}
              disabled={thanking || thanked}
              data-testid="load-timeline-thank-driver-btn"
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-wide transition-colors ${
                thanked
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 cursor-default"
                  : "bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
              } disabled:opacity-50`}
            >
              {thanking ? (
                <><CircleNotch size={11} className="animate-spin" /> Sending</>
              ) : thanked ? (
                <><CheckCircle size={11} weight="fill" /> Thanked</>
              ) : (
                <><ChatCircleDots size={11} weight="fill" /> Thank driver</>
              )}
            </button>
          </div>
        )}

        <div className="p-4">
          {loading ? (
            <div className="text-center text-sm text-white/50 py-8">Loading…</div>
          ) : !data?.events?.length ? (
            <div className="text-center text-sm text-white/50 py-8">
              No timeline events yet.
            </div>
          ) : (
            <ol className="relative border-l-2 border-white/10 ml-3 space-y-5 pl-6" data-testid="load-timeline-list">
              {data.events.map((ev, i) => {
                const style = EVENT_STYLES[ev.type] || EVENT_STYLES["load.updated"];
                const Icon = style.icon;
                const isDriverPod = ev.type === "driver_pod_uploaded";
                return (
                  <li key={i} className="relative" data-testid={`load-timeline-event-${i}`}>
                    <div className={`absolute -left-[35px] top-0 rounded-full bg-[#111] ring-4 ${style.ring} p-1.5`}>
                      <Icon size={12} weight="fill" className={style.color} />
                    </div>
                    <div className={`rounded-lg border p-3 ${
                      isDriverPod
                        ? "border-emerald-500/25 bg-emerald-500/[0.04]"
                        : "border-white/5 bg-white/[0.02]"
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className={`text-sm font-medium ${style.color}`}>
                          {ev.label}
                        </div>
                        <div className="text-[10px] text-white/40 shrink-0 tabular-nums">
                          {ev.at ? new Date(ev.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                        </div>
                      </div>
                      {ev.actor && (
                        <div className="text-[11px] text-white/50 mt-0.5">
                          {ev.auto ? (
                            <span
                              className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300"
                              data-testid={`load-timeline-auto-badge-${i}`}
                            >
                              AUTO · GEOFENCE
                            </span>
                          ) : (
                            ev.actor
                          )}
                          {isDriverPod && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">
                              DRIVER
                            </span>
                          )}
                        </div>
                      )}
                      {ev.auto && ev.detail?.reason_copy && (
                        <div className="text-[11px] text-cyan-300/80 mt-1">
                          {ev.detail.reason_copy}
                        </div>
                      )}
                      {ev.auto && ev.detail?.arrival_sms_status === "sent" && (
                        <div className="text-[10px] text-emerald-300/80 mt-1 inline-flex items-center gap-1">
                          <ChatCircleDots size={9} weight="fill" />
                          Arrival SMS sent to dispatch
                        </div>
                      )}
                      {ev.detail?.filename && (
                        <div className="text-[11px] text-white/60 mt-1 font-mono truncate">
                          {ev.detail.filename}
                        </div>
                      )}
                      {ev.type === "load.updated" && ev.detail?.changed && (
                        <div className="text-[10px] text-white/40 mt-1">
                          Changed: {(ev.detail.changed || []).slice(0, 5).join(", ")}
                        </div>
                      )}
                      {ev.type === "invoice_emailed" && ev.detail?.load_docs_attached > 0 && (
                        <div className="text-[10px] text-amber-300 mt-1">
                          {ev.detail.load_docs_attached} paperwork attached
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
